import { SerializedDate } from "../data/Data";

/**
 * 1. GENERATE: Creates a new Date object from year, month, and day numbers.
 * The date is set to midnight (00:00:00) in the user's local timezone.
 * @param {number} year - The full year (e.g., 2025).
 * @param {number} month - The month number (1-12).
 * @param {number} day - The day of the month (1-31).
 * @returns {Date | null} A new Date object, or null if the date is invalid.
 */
export function generateLocalDate(year: number, month: number, day: number): Date | null {
    // The month for the Date constructor is 0-indexed, so we subtract 1.
    const date = new Date(year, month - 1, day);

    // This check handles invalid inputs like a day of 32 or month of 13.
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        console.error("Invalid date value provided (e.g., month or day out of range).");
        return null;
    }

    return date;
}

/**
 * 2. SAVE: Converts a Date object to a full ISO 8601 string with the local timezone offset.
 * Note: We manually build this string instead of using .toISOString() because
 * .toISOString() always converts the date to the UTC timezone (ending in 'Z').
 * This method preserves the local time and shows its offset.
 * @param {Date} dateObject - The date object to convert.
 * @returns {string} The date in 'YYYY-MM-DDTHH:mm:ss.sss±hh:mm' format, or an empty string if invalid.
 */
export function saveDateToStringWithTimezone(dateObject: Date): SerializedDate {
    if (!(dateObject instanceof Date) || isNaN(dateObject.getTime())) {
        console.error("Invalid input: not a valid Date object.");
        return '' as SerializedDate;
    }

    // Get local date and time components
    const year = dateObject.getFullYear();
    const month = String(dateObject.getMonth() + 1).padStart(2, '0');
    const day = String(dateObject.getDate()).padStart(2, '0');
    const hours = String(dateObject.getHours()).padStart(2, '0');
    const minutes = String(dateObject.getMinutes()).padStart(2, '0');
    const seconds = String(dateObject.getSeconds()).padStart(2, '0');
    const milliseconds = String(dateObject.getMilliseconds()).padStart(3, '0');

    // --- CORRECTED TIMEZONE LOGIC ---
    const offset = dateObject.getTimezoneOffset();
    const offsetSign = offset > 0 ? '-' : '+'; // Note: getTimezoneOffset has an inverted sign
    const totalOffsetMinutes = Math.abs(offset);

    const offsetHours = Math.floor(totalOffsetMinutes / 60);
    const offsetMinutes = totalOffsetMinutes % 60;

    const offsetString = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
    // --- END CORRECTION ---

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${offsetString}` as SerializedDate;
}

/**
 * 3. LOAD: Converts a full ISO 8601 string (with or without timezone) into a Date object.
 * The native Date constructor is excellent at parsing this format.
 * @param {string} isoString - The date in ISO 8601 format.
 * @returns {Date | null} A Date object, or null if the string is invalid.
 */
export function loadDateFromISOString(isoString: SerializedDate): Date | null {
    const date = new Date(isoString);

    // Check if the created date is valid.
    if (isNaN(date.getTime())) {
        console.error("Invalid ISO string format.");
        return null;
    }

    return date;
}